import React from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, ListMinus, Settings, Users, Headphones } from "lucide-react";
import { NumberSwitcher } from "@/components/NumberSwitcher";
import { Dialer } from "@/components/Dialer";
import { TeamMembersList } from "@/components/TeamMembersList";
import { useAuth } from "@/context/AuthContext";

const ADMIN_LINKS = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/appels", label: "Appels récents", icon: ListMinus, testid: "nav-calls" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/parametres", label: "Paramètres", icon: Settings, testid: "nav-settings" },
];

// Agents: Appels, Contacts and Mon poste only (backend enforces the rest).
const AGENT_LINKS = [
  { to: "/appels", label: "Appels récents", icon: ListMinus, testid: "nav-calls" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/mon-poste", label: "Mon poste", icon: Headphones, testid: "nav-my-station" },
];

// Superviseurs: same as agents + the dashboard (no Réglages).
const SUPERVISEUR_LINKS = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  ...AGENT_LINKS,
];

// Full desktop sidebar — shown at lg+ only (unchanged desktop experience).
export const Sidebar = () => {
  const { user, isAdmin } = useAuth();
  const links = isAdmin ? ADMIN_LINKS : user?.role === "superviseur" ? SUPERVISEUR_LINKS : AGENT_LINKS;
  return (
    <aside
      data-testid="app-sidebar"
      className="hidden lg:flex w-64 fixed h-screen top-0 left-0 border-r border-slate-200 bg-white flex-col z-20"
    >
      <div className="p-3 space-y-2 border-b border-slate-100">
        <NumberSwitcher />
        <Dialer />
      </div>

      <div className="flex-1 overflow-y-auto">
        <nav className="px-3 py-4 space-y-1">
          {links.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 pb-4">
          {isAdmin && <TeamMembersList />}
        </div>
      </div>

      {isAdmin && (
        <div className="p-3 border-t border-slate-100">
          <NavLink
            to="/parametres"
            data-testid="manage-team-link"
            className="text-xs text-slate-400 hover:text-emerald-700 transition-colors px-2"
          >
            Gérer l'équipe dans Paramètres →
          </NavLink>
        </div>
      )}
    </aside>
  );
};
